import { CSSProperties, FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import styled, { css } from 'styled-components';
import { useLocation } from 'react-router';

import { Campaign, GameResult } from '../../types';
import { GameWidgetWrapper, GameWrapper, GameFormWrapper, ResultScreen } from '../common';
import { WheelGameData } from './types';
import { getWidgetBgDesktopImage, getWidgetBgMobileImage } from '../../lib';
import { Form } from '../common/form';
import { Wheel4, Wheel6, Wheel8, Wheel10, Wheel12, WheelPointer } from './components';

import { TApiError } from '../../components';
import { getCouponDisplayName } from '../common/utils';

import './styles.css';
import { AppRoutes } from '../../routes';

const withStyles = (Component: any) => styled(Component)`
  width: 320px;

  @media screen and (min-width: 768px) {
    width: 620px;
    height: 620px;
  }
`;

const StyledWheel4 = withStyles(Wheel4);
const StyledWheel6 = withStyles(Wheel6);
const StyledWheel8 = withStyles(Wheel8);
const StyledWheel10 = withStyles(Wheel10);
const StyledWheel12 = withStyles(Wheel12);

const getWheelComponent = (amount: number) => {
  switch (amount) {
    case 4:
      return StyledWheel4;
    case 6:
      return StyledWheel6;
    case 8:
      return StyledWheel8;
    case 10:
      return StyledWheel10;
    case 12:
      return StyledWheel12;
    default:
      return StyledWheel8;
  }
};

const FIVE_TURNS = 1800;

export type WheelGameProps = {
  campaign: Campaign;
  gameData: WheelGameData;
  isDesktop?: boolean;
  formEditor?: ReactNode;
  resultScreenEditor?: ReactNode;
  gameResult?: GameResult;
  gameResultFetching: boolean;
  gameResultError?: TApiError;
  resultScreenVisible: boolean;
  editable: boolean;
  onAnimationEnd: () => void;
  onCloseResultScreen: () => void;
  onButtonClick?: () => void;
};

export const WheelGame: FC<WheelGameProps> = ({
  gameData,
  campaign,
  formEditor,
  resultScreenEditor,
  gameResult,
  resultScreenVisible,
  gameResultError,
  editable,
  gameResultFetching,
  onAnimationEnd,
  onButtonClick,
  onCloseResultScreen,
  ...restProps
}) => {
  const isMediaDesktop = useMediaQuery({ minWidth: 768 });
  const isDesktop = restProps.isDesktop ?? isMediaDesktop;
  const reqId = useRef(0);
  const wheelCurrentAngle = useRef(0);
  const idleAnimation = useRef(true);
  const rotationContainerRef = useRef<HTMLDivElement>(null);
  const oneSlotAngle = 360 / gameData.slotsAmount;
  const location = useLocation();

  const resetWheel = useCallback(() => {
    if (rotationContainerRef.current) {
      rotationContainerRef.current.style.transition = 'none';
      rotationContainerRef.current.style.transform = `rotate(0deg)`;
      idleAnimation.current = true;
    }
  }, []);

  const onClose = useCallback(() => {
    onCloseResultScreen();
  }, [onCloseResultScreen]);

  const getWidgetStyles = (): CSSProperties => {
    const props: CSSProperties = {
      backgroundColor: campaign.colors.bg,
    };

    if (campaign.useBgImage) {
      let bgImageUrl = isDesktop
        ? getWidgetBgDesktopImage(campaign.bgImageUrl.desktop)
        : getWidgetBgMobileImage(campaign.bgImageUrl.mobile || campaign.bgImageUrl.desktop);

      props.backgroundImage = `url(${
        isDesktop ? getWidgetBgDesktopImage(bgImageUrl) : getWidgetBgMobileImage(bgImageUrl)
      })`;
    }

    return props;
  };

  useEffect(() => {
    if (!gameResult || !rotationContainerRef.current) {
      return;
    }

    const { couponIndex } = gameResult;

    if (couponIndex === undefined) {
      onAnimationEnd();
      idleAnimation.current = false;
      return;
    }

    const targetAngle = FIVE_TURNS - oneSlotAngle * couponIndex;

    idleAnimation.current = false;
    rotationContainerRef.current.style.transform = `rotate(${targetAngle}deg) translateZ(0)`;
    rotationContainerRef.current.style.transition = 'transform 5s ease';
  }, [gameResult, onAnimationEnd, oneSlotAngle]);

  const handleTransitionEnd = useCallback(() => {
    setTimeout(() => {
      onAnimationEnd();
    }, 300);
  }, [onAnimationEnd]);

  const rotate = useCallback(() => {
    if (!rotationContainerRef.current || !idleAnimation.current) {
      return;
    }

    wheelCurrentAngle.current += 0.25;

    if (wheelCurrentAngle.current > 360) {
      wheelCurrentAngle.current = wheelCurrentAngle.current - 360;
    }

    rotationContainerRef.current.style.transform = `rotate(${wheelCurrentAngle.current}deg) translateZ(0)`;

    reqId.current = window.requestAnimationFrame(rotate);
  }, []);

  useEffect(() => {
    if (!resultScreenVisible) {
      resetWheel();
    }
  }, [resultScreenVisible, resetWheel]);

  useEffect(() => {
    if (!editable && !resultScreenVisible) {
      reqId.current = window.requestAnimationFrame(rotate);
    }

    return () => {
      window.cancelAnimationFrame(reqId.current);
    };
  }, [editable, rotate, resultScreenVisible]);

  const WheelComponent = getWheelComponent(gameData.slotsAmount);
  const isButtonLoading = Boolean(gameResultFetching || gameResult);

  const coupons = campaign.wixCoupons;

  const shouldReduceFieldsSize =
    Boolean(campaign.wixInstanceId) && AppRoutes.EMBED.match(location.pathname) && !isDesktop;

  return (
    <GameWidgetWrapper style={getWidgetStyles()}>
      {!resultScreenVisible && (
        <>
          <GameWrapper>
            <WheelWrapper
              /** className не удалять, используется в ./styles.css */
              className="wheel-wrapper"
            >
              <RotationContainerWrapper className="rotation-container-wrapper" small={shouldReduceFieldsSize}>
                <RotationContainer ref={rotationContainerRef} onTransitionEnd={handleTransitionEnd}>
                  <WheelComponent gameData={gameData} className="eg-wheel-svg" />

                  {coupons.map((coupon, index) => {
                    if (coupon.deleted || !coupon.active) {
                      return null;
                    }

                    return (
                      <CouponText key={index} rotate={oneSlotAngle * index - 90} className="eg-coupon-text">
                        <CouponTextInner
                          color={index % 2 === 0 ? gameData.colors.primary.text : gameData.colors.secondary.text}
                          className="eg-coupon-text-inner"
                        >
                          {getCouponDisplayName(coupon)}
                        </CouponTextInner>
                      </CouponText>
                    );
                  })}
                </RotationContainer>
              </RotationContainerWrapper>

              <StyledWheelPointer color={gameData.colors.tertiary} />
            </WheelWrapper>
          </GameWrapper>

          <WheelGameFormWrapper>
            {formEditor || (
              <Form
                onButtonClick={onButtonClick}
                buttonDisabled={isButtonLoading}
                buttonLoading={isButtonLoading}
                gameResultError={gameResultError}
              />
            )}
          </WheelGameFormWrapper>
        </>
      )}

      {resultScreenEditor || (
        <ResultScreen
          visible={resultScreenVisible}
          gameResult={gameResult}
          onClose={onClose}
          gameResultError={gameResultError}
        />
      )}
    </GameWidgetWrapper>
  );
};

const WheelGameFormWrapper = styled(GameFormWrapper)`
  margin-top: -115px;

  @media screen and (min-width: 768px) {
    margin-top: 0;
  }
`;

const WheelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  font-size: 12px;
  overflow: hidden;
  transform: translateY(-115px);

  @media screen and (min-width: 768px) {
    width: 620px;
    height: 100%;
    font-size: 16px;
    transform: none;
  }
`;

const RotationContainerWrapper = styled.div<{ small: boolean }>`
  ${(p) =>
    p.small
      ? css`
          transform: scale(1.2);
        `
      : css`
          transform: scale(1.5);
          margin-bottom: 48px;
        `};

  overflow: hidden;

  @media screen and (min-width: 768px) {
    transform: scale(1);
    margin-bottom: 0;
  }
`;

const RotationContainer = styled.div`
  display: flex;
  width: 100%;
  transform: rotate(0deg) translateZ(0);
`;

const StyledWheelPointer = styled(WheelPointer)`
  position: relative;
  z-index: 1;
  top: -12px;
  width: 30px;
  height: 30px;

  @media screen and (min-width: 768px) {
    top: -60px;
  }
`;

const CouponText = styled.div<{ rotate: number }>`
  position: absolute;
  padding: 0 24px;
  width: 100%;
  top: 50%;
  transform: translateY(-50%) rotate(${(p) => p.rotate}deg);
  user-select: none;

  @media screen and (min-width: 768px) {
    padding: 0 48px;
  }
`;

const CouponTextInner = styled.div<{ color: string }>`
  max-width: calc(100% / 2 - 40px);
  text-align: center;
  font-weight: 500;
  color: ${(p) => p.color};
  max-height: 50px;
  overflow: hidden;

  @media screen and (min-width: 768px) {
    max-width: calc(100% / 2 - 76px);
  }
`;

export default WheelGame;
